/**
 * Paleta y vocabulario visual.
 *
 * Fuente única de los colores que el §7 exige consistentes entre la tabla y el
 * grafo. Los valores son hex y no variables CSS a propósito: Cytoscape pinta
 * sobre canvas en la Fase 5 y necesita colores resueltos, no tokens que solo
 * el navegador sepa interpretar.
 *
 * Los tonos están elegidos con luminosidad media para que contrasten tanto
 * sobre fondo claro como oscuro sin tener dos paletas que mantener.
 */

import type { Nivel, Postura, Prioridad, TipoRelacion, TipoStakeholder } from '../api/tipos'

/** Color por tipo de stakeholder. Agrupa por familia: lo estatal en azules,
 *  la sociedad civil en verdes, lo privado en magentas. */
export const COLOR_TIPO: Record<TipoStakeholder, string> = {
  institucion_publica: '#3b6fd4',
  judicial: '#6b5bd6',
  academia: '#4a9fd8',
  organizacion: '#0e9aa7',
  cliente: '#17a398',
  ong: '#2f9e5f',
  sindicato: '#7fa42b',
  gremio: '#c9971f',
  comunidad: '#e07b39',
  medio: '#d9455f',
  empresa: '#b8479c',
  inversionista: '#8a5cd1',
  otro: '#8a8f98',
}

export const ETIQUETA_TIPO: Record<TipoStakeholder, string> = {
  organizacion: 'Organización',
  institucion_publica: 'Institución pública',
  comunidad: 'Comunidad',
  empresa: 'Empresa',
  gremio: 'Gremio',
  judicial: 'Judicial',
  medio: 'Medio',
  academia: 'Academia',
  ong: 'ONG',
  sindicato: 'Sindicato',
  inversionista: 'Inversionista',
  cliente: 'Cliente',
  otro: 'Otro',
}

/** Color por postura: rojo opositora → verde favorable → gris desconocida (§7). */
export const COLOR_POSTURA: Record<Postura, string> = {
  opositora: '#d1495b',
  critica: '#e08c3b',
  neutral: '#8a8f98',
  colaborativa: '#2b9e8f',
  favorable: '#3a9e5c',
  desconocida: '#b0b5bd',
}

/**
 * Variable CSS equivalente a cada postura.
 *
 * Los hex de `COLOR_POSTURA` son para el canvas del grafo, que no entiende
 * variables. En la interfaz se usan estas, que tienen versión clara y oscura:
 * un mismo hex no puede tener contraste suficiente sobre blanco y sobre negro.
 */
export const VAR_POSTURA: Record<Postura, string> = {
  opositora: 'var(--color-opositora)',
  critica: 'var(--color-critica)',
  neutral: 'var(--color-neutral)',
  colaborativa: 'var(--color-colaborativa)',
  favorable: 'var(--color-favorable)',
  desconocida: 'var(--color-desconocida)',
}

export const ETIQUETA_POSTURA: Record<Postura, string> = {
  opositora: 'Opositora',
  critica: 'Crítica',
  neutral: 'Neutral',
  colaborativa: 'Colaborativa',
  favorable: 'Favorable',
  desconocida: 'Desconocida',
}

export const ETIQUETA_NIVEL: Record<Nivel, string> = {
  muy_bajo: 'Muy bajo',
  bajo: 'Bajo',
  medio: 'Medio',
  alto: 'Alto',
  muy_alto: 'Muy alto',
}

/** Orden de los niveles, para comparar y para escalar tamaños. */
export const ORDEN_NIVEL: Record<Nivel, number> = {
  muy_bajo: 0,
  bajo: 1,
  medio: 2,
  alto: 3,
  muy_alto: 4,
}

export const NIVELES: Nivel[] = ['muy_bajo', 'bajo', 'medio', 'alto', 'muy_alto']

/** Diámetro del nodo en el grafo, proporcional a la influencia (§7). */
export function tamanoNodo(nivel: Nivel | undefined): number {
  return 26 + (ORDEN_NIVEL[nivel ?? 'medio'] ?? 2) * 11
}

/** Grosor del borde, proporcional al impacto recibido (§7). */
export function grosorBorde(nivel: Nivel | undefined): number {
  return 1.5 + (ORDEN_NIVEL[nivel ?? 'medio'] ?? 2) * 1.25
}

export const ETIQUETA_RELACION: Record<TipoRelacion, string> = {
  regula: 'regula',
  financia: 'financia',
  contrata: 'contrata',
  representa: 'representa',
  litiga_contra: 'litiga contra',
  colabora_con: 'colabora con',
  depende_de: 'depende de',
  opone_a: 'se opone a',
  pertenece_a: 'pertenece a',
}

export const ETIQUETA_PRIORIDAD: Record<Prioridad, string> = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
}

export const COLOR_PRIORIDAD: Record<Prioridad, string> = {
  critica: '#d1495b',
  alta: '#e08c3b',
  media: '#c9971f',
  baja: '#8a8f98',
}

/** Color del nodo central de la entidad: distinto de cualquier tipo (§7). */
export const COLOR_ENTIDAD = '#1f2937'
export const COLOR_ENTIDAD_CLARO = '#e5e7eb'

/**
 * Versión translúcida de un color, para fondos de badge.
 * Se usa `color-mix` para no duplicar cada tono en dos variantes.
 */
export function conAlfa(hex: string, porcentaje: number): string {
  return `color-mix(in srgb, ${hex} ${porcentaje}%, transparent)`
}
